import { SkyButton, SkyLoader, SkyMessageBar, SkyProvider } from "@skypointcloud/skypoint-ui";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { Redirect, Route, Router, Switch, useLocation, useSearch } from "wouter";
import { DestinationReadinessBanner } from "@/components/destination/DestinationReadinessBanner";
import { Sidebar } from "@/components/navigation/Sidebar";
import { TopBar } from "@/components/navigation/TopBar";
import { OverviewLanding } from "@/components/overview/OverviewLanding";
import { AppFooter } from "@/components/shell/AppFooter";
import { FeatureOffNotice } from "@/components/shell/FeatureOffNotice";
import { FullPageLoader } from "@/components/shell/FullPageLoader";
import { IdleTimeoutDialog } from "@/components/shell/IdleTimeoutDialog";
import { InstanceUnavailable } from "@/components/shell/InstanceUnavailable";
import { InstanceMismatchRecovery } from "@/components/shell/InstanceMismatchRecovery";
import { PageHeader } from "@/components/shell/PageHeader";
import { PageCrumbProvider } from "@/components/shell/PageCrumb";
import { ShellBreadcrumb } from "@/components/shell/ShellBreadcrumb";
import { SignInStepPage, useRememberBrowserAfterStepUp } from "@/components/shell/SignInStepPage";
import { SkipLink } from "@/components/shell/SkipLink";
import { TopbarSlotProvider } from "@/components/shell/TopbarSlotContext";
import { StepUpHost } from "@/components/settings/StepUpHost";
import { ThemeProvider, readStoredPreference } from "@/contexts/ThemeContext";
import { TenantInstanceProvider, useTenantInstance } from "@/contexts/TenantInstanceContext";
import { useGroupAccessEnabled } from "@/hooks/use-group-access";
import { useIdleTimeout } from "@/hooks/use-idle-timeout";
import { useMcpWriter } from "@/hooks/use-mcp";
import { pickerPathFor } from "@/hooks/use-return-to";
import { usePermissions } from "@/hooks/use-permissions";
import { AuthProvider, useAuth } from "@/lib/auth/AuthContext";
import { NoticeProvider, useNotice, useNoticeState } from "@/lib/feedback/NoticeProvider";
import { NoticeRegion } from "@/lib/feedback/NoticeRegion";
import { InstanceRunFeedProvider } from "@/lib/live/InstanceRunFeed";
import { InstanceRouter } from "@/lib/InstanceRouter";
import { instancePath, searchWithoutInstance, splitInstancePath } from "@/lib/instancePath";
import { reauthReturnTo } from "@/lib/auth/reauthHandoff";
import { useAutoSignIn } from "@/lib/auth/useAutoSignIn";
import { ApiClientError } from "@/lib/api";
import { MCP_CONSENT_PATH } from "@shared/mcp/consent";
import { beginUserAction } from "@/lib/correlation";
import { reactPlugin, withAITracking } from "@/lib/logger";
import { queryClient } from "@/lib/queryClient";
import {
  APP_ROUTES,
  derivePageTitle,
  featureAllows,
  featureHiddenWhenOff,
  firstAllowedPath,
  isGlobalPath,
  isOverviewLanding,
  legacyAddress,
  locationNeedsNoInstance,
  readsAtPlatformScope,
  routeDescription,
  routeFor,
  visibleRoute,
  ROUTES,
} from "@/lib/routes";
import CallbackPage from "@/pages/callback";
import ConnectionsPage from "@/pages/connections";
import DestinationPage from "@/pages/destination";
import InstancesPage from "@/pages/instances";
import NotFoundPage from "@/pages/not-found";
import OverviewPage from "@/pages/overview";
import RunsPage from "@/pages/runs";
import SchedulesPage from "@/pages/schedules";
import SignInPage from "@/pages/sign-in";
import TransformationsPage from "@/pages/transformations";
import YourDataPage from "@/pages/your-data";
import MonitorPage from "@/pages/monitor";
import McpConsentPage, { McpConsentPlatformRefusal } from "@/pages/mcp-consent";
import McpApprovePage from "@/pages/mcp-approve";
import AdminAiAppsPage from "@/pages/admin/ai-apps";
import AdminAuditPage from "@/pages/admin/audit";
import ConnectedAppsPage from "@/pages/connected-apps";
import MyAlertsPage from "@/pages/my-alerts";
import AdminConnectorsPage from "@/pages/admin/connectors";
import AdminCustomersRedirect from "@/pages/admin/customers";
import AdminInstancesPage from "@/pages/admin/instances";
import AdminPlatformSettingsPage from "@/pages/admin/platform-settings";
import AdminSettingsPage from "@/pages/admin/settings";
import AdminTenantsPage from "@/pages/admin/tenants";
import AdminUsersPage from "@/pages/admin/users";

/** Every declared route's screen, keyed by the path the route table declares. */
const PAGES: Record<string, ComponentType> = {
  "/overview": OverviewPage,
  "/connections": ConnectionsPage,
  "/schedules": SchedulesPage,
  "/runs": RunsPage,
  "/transformations": TransformationsPage,
  "/your-data": YourDataPage,
  "/monitor": MonitorPage,
  "/settings/destination": DestinationPage,
  "/admin/users": AdminUsersPage,
  "/admin/instances": AdminInstancesPage,
  "/admin/connectors": AdminConnectorsPage,
  "/settings": AdminSettingsPage,
  "/admin/audit": AdminAuditPage,
  "/admin/customers": AdminCustomersRedirect,
  "/admin/tenants": AdminTenantsPage,
  "/admin/platform-settings": AdminPlatformSettingsPage,
  "/admin/ai-apps": AdminAiAppsPage,
  "/connected-apps": ConnectedAppsPage,
  "/mcp-approve": McpApprovePage,
  "/my-alerts": MyAlertsPage,
};

/**
 * What a user sees at a route they can reach but lack the permission for
 * (FR-13). The server would refuse the data anyway; saying so here is kinder
 * than an empty table, and it names the permission so an admin can grant it, unless the route
 * says who it is for instead (`deniedMessage`, #1067: Users comes with a role, not a grant).
 */
function PermissionDenied({ permission, message }: { permission: string; message?: string }) {
  return (
    <SkyMessageBar
      intent="warning"
      layout="multiline"
      title="You do not have access to this screen"
      message={
        message ??
        `This screen needs the ${permission} permission in this instance. Ask an administrator to grant it.`
      }
      testId="permission-denied"
    />
  );
}

/** The routed area inside the shell. */
function ShellRoutes() {
  const { canAny } = usePermissions();
  const { me } = useTenantInstance();
  const features = me?.features;
  const [location] = useLocation();
  const search = useSearch();

  // B-51a: a Settings tab's old address goes to its new one (`legacyAddress`).
  const moved = legacyAddress(location);
  if (moved) return <Redirect to={`${moved}${search ? `?${search}` : ""}`} replace />;

  return (
    <Switch>
      <Route path="/">
        <Redirect to={firstAllowedPath((permission) => canAny(permission))} />
      </Route>
      {APP_ROUTES.map((route) => {
        // A route whose feature is off has no nav entry, before any permission check: there is
        // nothing to hold a permission for. An address that reaches skyMonitor says it is off;
        // one that reaches AI apps reads as an unknown address (#1812, `featureHiddenWhenOff`).
        if (route.feature && !featureAllows(route, features)) {
          return (
            <Route key={route.path} path={route.path} nest>
              {featureHiddenWhenOff(route.feature) ? (
                <NotFoundPage />
              ) : (
                <FeatureOffNotice feature={route.feature} />
              )}
            </Route>
          );
        }
        const Page = PAGES[route.path] ?? NotFoundPage;
        const allowed = !route.anyPermission || canAny(...route.anyPermission);
        return (
          <Route key={route.path} path={route.path} nest>
            {allowed ? (
              <Page />
            ) : (
              <PermissionDenied
                permission={route.anyPermission?.[0] ?? "required"}
                message={route.deniedMessage}
              />
            )}
          </Route>
        );
      })}
      <Route>
        <NotFoundPage />
      </Route>
    </Switch>
  );
}

/**
 * The authenticated shell (UX-1, GUIDELINES.md §3.4).
 *
 * `h-screen overflow-hidden` bounds the shell to the viewport so the document
 * never scrolls and the fixed top bar stays genuinely full-width; scrolling
 * happens inside the container below instead. The sidebar and the content
 * column are siblings in ONE flex row, so the drawer's header row and the
 * breadcrumb row share a layout flow and their bottom dividers line up exactly: a
 * fixed sidebar beside in-flow content drifts by a few pixels across browsers,
 * zoom levels and device pixel ratios.
 *
 * The content column, top to bottom (client/AGENTS.md "Page layout"): the
 * breadcrumb row, the page header (the one H1), notices, then the scroll
 * container holding the full-width page body and the footer.
 */
function AppShell({ unavailable = null }: { unavailable?: "checking" | "refused" | null }) {
  const [location] = useLocation();
  // #1812: an AI apps address while they are off has no header of its own, like any unknown one.
  const { me, selectedInstance, switching } = useTenantInstance();
  const declared = unavailable ? undefined : routeFor(location);
  const route = visibleRoute(declared, me?.features);
  const title = unavailable
    ? "Instance not available"
    : (route?.title ?? derivePageTitle(location));
  // A subtitle may say more while group access is on (r5 polish: Tenants and its directory).
  const groupAccess = useGroupAccessEnabled();
  // And it may say more to a reader at platform scope (round 6 W17: Instances of every tenant).
  const { can } = usePermissions();
  const platformScope = readsAtPlatformScope(route, (permission) => can(permission));
  // #1393 finding 61: Connected apps says "read" only for a reader with no write permission and
  // no listed connection that can change (`useMcpWriter`); its grants are read on that page only.
  const mcpWriter = useMcpWriter(route?.path === ROUTES.connectedApps);

  return (
    // `app-shell` is what e2e/walkthrough.spec.ts waits for to know the
    // authenticated shell has rendered.
    <div className="min-h-screen bg-background" data-testid="app-shell">
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      <SkipLink href="#navigation">Skip to navigation</SkipLink>

      <TopBar />

      <TopbarSlotProvider>
        <PageCrumbProvider>
          <div className="flex h-screen overflow-hidden pt-12">
            <Sidebar />
            <div className="flex min-w-0 flex-1 flex-col">
              <ShellBreadcrumb />
              <PageHeader
                title={title}
                description={routeDescription(route, { groupAccess, platformScope, mcpWriter })}
                noHelp={declared !== undefined && route === undefined}
              />
              {/* Session 10 (ux-s10 §3): confirmations land here, in flow under the page title and
                outside the scroll container, never in a corner over the page. */}
              <NoticeRegion />
              <main id="main-content" className="min-h-0 flex-1" role="main" tabIndex={-1}>
                {/* The scroll container stays FULL WIDTH so its scrollbar sits flush against the
                  window edge. The page body is NOT capped or centred (owner, 2026-09-28: "empty
                  space on the left and right side of the grid"; SKILL "Content is
                  left-aligned"): grids and tables use the whole column at every width, and a
                  form caps its own fields (`FORM_MAX_WIDTH`). The column is at least the
                  viewport tall, so the footer sits at the bottom of a short page. */}
                <div className="app-scroll h-full w-full overflow-y-auto">
                  <div className="flex min-h-full flex-col">
                    <div className="min-w-0 flex-1 p-4" data-testid="shell-page-body">
                      {/* Keyed by instance: a switch in place remounts every page, draft and
                        observer, so nothing seeded from the old instance survives (client/AGENTS.md
                        "Switching instance"). */}
                      {unavailable === "refused" ? (
                        <InstanceUnavailable />
                      ) : unavailable === "checking" ? (
                        <SkyLoader
                          label="Checking your access to this instance…"
                          testId="instance-checking"
                        />
                      ) : switching ? null : (
                        // Nothing of a page renders in the frame of a change of instance, before
                        // the cache is reset (`TenantInstanceContext`), so no page of the new
                        // instance reads what the old one left.
                        <>
                          {/* #375, #923: why nothing runs, on every instance screen, every role. */}
                          {selectedInstance && !isGlobalPath(location) ? (
                            <DestinationReadinessBanner key={`readiness-${selectedInstance.id}`} />
                          ) : null}
                          <ShellRoutes key={selectedInstance?.id ?? "none"} />
                        </>
                      )}
                    </div>
                    <AppFooter />
                  </div>
                </div>
              </main>
            </div>
          </div>
        </PageCrumbProvider>
      </TopbarSlotProvider>
    </div>
  );
}

/**
 * Where the return from the identity provider puts the user down (FR-14).
 *
 * Reading the staged entry is NOT a pure question: `reauthReturnTo` drops the
 * entry when it was staged by another account or against another instance,
 * which is what stops one person's draft — their credential — being replayed
 * for the next. A drop like that in a render body runs on every render of this
 * component and on renders React throws away, so an entry could be destroyed
 * by a render that never commits, and the unsaved work it was carrying would
 * be gone with it.
 *
 * So the read happens in a layout effect, once per arrival, behind a ref that
 * survives React's double-invoked mount in development. The first frame shows
 * the loader the sign-in already shows rather than redirecting to "/" and
 * bouncing again once the answer arrives.
 */
function ReauthRedirect({
  account,
  instanceId,
}: {
  account: string | null;
  instanceId: string | null;
}) {
  const consumed = useRef(false);
  const [target, setTarget] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (consumed.current) return;
    consumed.current = true;
    setTarget(reauthReturnTo(account, instanceId) ?? "/");
  }, [account, instanceId]);

  if (target === null) return <FullPageLoader message="Signing you in…" />;
  return <Redirect to={target} />;
}

/** Not signed in: the callback, the redirect to sign-in, or the sign-in page itself. */
function signedOutView(location: string, autoSignIn: ReturnType<typeof useAutoSignIn>) {
  if (location === "/callback") return <CallbackPage />;
  if (autoSignIn.phase === "redirecting") {
    return (
      <FullPageLoader
        message={
          autoSignIn.expired
            ? "Your session ended after a period of inactivity. Taking you to sign-in…"
            : "Taking you to sign-in…"
        }
      />
    );
  }
  return <SignInPage notice={autoSignIn.notice} />;
}

/** A page outside the shell (the instance picker, the MCP consent page): the top bar and the page. */
function standaloneView(page: ReactNode, idleDialog: ReactNode, instanceId: string | null = null) {
  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      {/* Keyed by instance, like the shell's routes: a switch in place remounts the page. */}
      <div className="pt-12" key={instanceId ?? "none"}>
        {/* B-13: the picker says why it opened when an instance's access ended mid-session. */}
        <NoticeRegion />
        {page}
      </div>
      {idleDialog}
    </div>
  );
}

/**
 * Sign-ins the server refuses for a reason the person can act on, not an outage:
 * - GROUP_REQUIRED (group access, 2026-09-28): the person's organisation requires its skyData
 *   directory group and their sign-in carries none of its groups.
 * - WORKFORCE_SIGN_IN_REQUIRED (owner rule, 2026-09-28): a PlatformAdmin signs in with a Skypoint
 *   work account only; a Skypoint operator moves the access.
 * - ACCESS_NOT_GRANTED (B-07, round 9): the identity provider signed the person in, but skyData
 *   holds no access for that identity (no grant, deactivated, or bound to another sign-in). Not
 *   an expired session: answering it with a sign-in redirect looped for ever.
 */
const BLOCKED_ACCESS: ReadonlyMap<string, { testId: string; title: string }> = new Map([
  [
    "GROUP_REQUIRED",
    {
      testId: "group-required",
      title: "Your access needs your organisation's skyData group",
    },
  ],
  [
    "ACCESS_NOT_GRANTED",
    {
      testId: "access-not-granted",
      title: "You do not have access to skyData",
    },
  ],
  [
    "WORKFORCE_SIGN_IN_REQUIRED",
    {
      testId: "workforce-sign-in-required",
      title: "Platform administrator access needs a work account",
    },
  ],
]);

/** A refused sign-in: the server's sentence and Sign out. */
function blockedAccessView({
  testId,
  title,
  message,
  onSignOut,
  idleDialog,
}: {
  testId: string;
  title: string;
  message: string;
  onSignOut: () => void;
  idleDialog: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl space-y-4 p-6 pt-20">
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          testId={testId}
          title={title}
          message={message}
        />
        <SkyButton appearance="primary" testId={`${testId}-sign-out`} onClick={onSignOut}>
          Sign out
        </SkyButton>
      </div>
      {idleDialog}
    </div>
  );
}

type TenantInstanceState = ReturnType<typeof useTenantInstance>;

/**
 * The access answers that stop the app before any routing: a sign-in the server refuses for a
 * reason the person can act on, a second factor still to give, or `/me` failing. Null when none
 * applies. Called, not rendered as a component, so the element tree is the page's own.
 */
function accessProblemView({
  error,
  me,
  signOut,
  idleDialog,
}: {
  error: TenantInstanceState["error"];
  me: TenantInstanceState["me"];
  signOut: ReturnType<typeof useAuth>["signOut"];
  idleDialog: ReactNode;
}): ReactNode {
  // Group access (2026-09-28) and the work-account rule for a PlatformAdmin (2026-09-28): not an
  // outage, so a plain message and a way out.
  const blocked = error instanceof ApiClientError ? BLOCKED_ACCESS.get(error.code) : undefined;
  if (blocked && error) {
    // Called, not rendered as a component, so the element tree is the page's own.
    return blockedAccessView({
      testId: blocked.testId,
      title: blocked.title,
      message: error.message,
      onSignOut: () => void signOut("user"),
      idleDialog,
    });
  }

  // Admin MFA (design 4.3): every other call would be refused until the person signs in again.
  if (me?.signInStep) {
    return (
      <>
        <SignInStepPage step={me.signInStep} mfa={me.mfa} />
        {idleDialog}
      </>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <TopBar />
        <div className="mx-auto max-w-2xl p-6 pt-20">
          <SkyMessageBar
            intent="error"
            layout="multiline"
            title="We could not load your access"
            message={error.message}
          />
        </div>
        {idleDialog}
      </div>
    );
  }

  return null;
}

/**
 * Inside an instance's address (`/i/<id>/...`): the redirect to the address every link uses, or
 * the shell in that instance. Null for any other location. Called, not rendered.
 */
function instanceAddressView({
  location,
  search,
  addressInstance,
  idleDialog,
}: {
  location: string;
  search: string;
  addressInstance: TenantInstanceState["addressInstance"];
  idleDialog: ReactNode;
}): ReactNode {
  const { instanceId: addressId, inner, segment } = splitInstancePath(location);
  if (addressId === null) return null;
  // One address per instance: an id written in capitals is the same instance, so it goes to
  // the address every link uses (the router compares the prefix as written).
  if (segment !== null && segment !== addressId) {
    return (
      <Redirect to={`${instancePath(addressId, inner)}${search ? `?${search}` : ""}`} replace />
    );
  }
  // A screen outside every instance is not under one: its own address.
  if (isGlobalPath(inner)) {
    return <Redirect to={`${inner}${search ? `?${search}` : ""}`} replace />;
  }
  const status = addressInstance?.status ?? "refused";
  return (
    <InstanceRouter instanceId={addressId} segment={segment ?? undefined}>
      <AppShell unavailable={status === "ok" ? null : status} />
      {idleDialog}
    </InstanceRouter>
  );
}

/**
 * An instance-scoped address without `/i/` (a bookmark, a link sent before T-58) goes to the
 * same screen in an instance: the one its `?instance=` names (a notification's link, which keeps
 * working; that instance's access is then checked like any other address), else the one this
 * tab was last in. With none (null), the landing and the picker decide as before.
 */
function legacyAddressRedirect({
  location,
  search,
  pendingInstanceId,
  selectedInstance,
}: {
  location: string;
  search: string;
  pendingInstanceId: TenantInstanceState["pendingInstanceId"];
  selectedInstance: TenantInstanceState["selectedInstance"];
}): ReactNode {
  if (isGlobalPath(location)) return null;
  if (pendingInstanceId) {
    return (
      <Redirect
        to={`${instancePath(pendingInstanceId, location)}${searchWithoutInstance(search)}`}
        replace
      />
    );
  }
  if (selectedInstance) {
    return (
      <Redirect
        to={`${instancePath(selectedInstance.id, location)}${search ? `?${search}` : ""}`}
        replace
      />
    );
  }
  return null;
}

/**
 * A signed-in person outside every instance's address: the MCP consent page, the admin landing,
 * the picker, or a platform or personal screen. Called, not rendered.
 */
function signedInScreenView({
  location,
  search,
  selectedInstance,
  hasPlatformScope,
  mcpEnabled,
  canAny,
  idleDialog,
}: {
  location: string;
  search: string;
  selectedInstance: TenantInstanceState["selectedInstance"];
  hasPlatformScope: boolean;
  /** `features.mcp` from `GET /me`: off, the consent page reads as an unknown address (#1812). */
  mcpEnabled: boolean;
  canAny: ReturnType<typeof usePermissions>["canAny"];
  idleDialog: ReactNode;
}): ReactNode {
  // Derived from the route table's permission metadata, so a new platform screen
  // is reachable the moment it declares a platform-scope permission and an
  // instance-scoped one can never slip through by naming itself `/admin/*`.
  const needsNoInstance = locationNeedsNoInstance(location, (permission) => canAny(permission));

  // The MCP consent page (ADR 0009 section 3): its own frame, outside the shell, in the instance
  // the grant will name. No instance yet: the picker first, coming back here (`returnTo`), except
  // for a platform administrator, who is refused before anything is asked.
  if (location === MCP_CONSENT_PATH) {
    // #1812: AI apps off or not set up, the address is one skyData does not have.
    if (!mcpEnabled) return standaloneView(<NotFoundPage />, idleDialog);
    if (!selectedInstance && hasPlatformScope) {
      return standaloneView(<McpConsentPlatformRefusal />, idleDialog);
    }
    if (!selectedInstance) return <Redirect to={pickerPathFor(location, search)} />;
    return standaloneView(<McpConsentPage />, idleDialog, selectedInstance?.id ?? null);
  }

  // The admin landing (Session 10, B1): on `/` or `/overview` with no instance chosen, go into
  // the first instance the user can read, or show Overview's "No instance yet". Every other
  // location keeps the picker redirect below.
  if (!selectedInstance && isOverviewLanding(location)) {
    return <OverviewLanding idleDialog={idleDialog} />;
  }

  // The requested location rides along as `returnTo`, so a notification's run link opened with no
  // instance chosen reaches its run after the pick (PR review, behaviour finding 2).
  if (!selectedInstance && location !== "/instances" && !needsNoInstance) {
    return <Redirect to={pickerPathFor(location, search)} />;
  }

  if (location === "/instances")
    return standaloneView(<InstancesPage />, idleDialog, selectedInstance?.id ?? null);

  // A platform or personal screen. With an instance selected its nav links go straight into it.
  const shell = (
    <>
      <AppShell />
      {idleDialog}
    </>
  );
  return selectedInstance ? (
    <InstanceRouter instanceId={selectedInstance.id}>{shell}</InstanceRouter>
  ) : (
    shell
  );
}

/**
 * Decides which of the three shapes the app is in: signing in, choosing an
 * instance, or working. Everything that needs an instance redirects to the
 * picker rather than rendering with a missing `x-instance-id` header, which
 * would 401 every request and look like a broken screen.
 *
 * "Everything that needs an instance" is not "everything". The platform
 * administration screens act on the deployment — tenants, instances,
 * connector definitions, platform settings, the platform audit trail — and their
 * routes read no instance header at all. Redirecting those to the picker too is
 * what made a FRESH deployment unusable: there are no instances to choose, so
 * the one person who can create the first one was sent to a screen that told
 * them to ask an administrator for access they already hold (FR-20).
 */
function AppContent() {
  const { status, account, signOut } = useAuth();
  const { isLoading, error, selectedInstance, me, addressInstance, pendingInstanceId } =
    useTenantInstance();
  // Admin MFA: after a sign-in that proved a second factor, remember the browser if asked.
  useRememberBrowserAfterStepUp(me?.mfa);
  const { canAny, hasPlatformScope } = usePermissions();
  const [location] = useLocation();
  const search = useSearch();
  const idle = useIdleTimeout(status === "signed-in");
  // An unauthenticated visit belongs at the identity provider, not in front of a
  // button that sends it there (FR-10). The hook owns that decision and the
  // loop guards around it; here it only chooses between a loader and the
  // fallback page.
  const autoSignIn = useAutoSignIn(location);

  // One correlation id per navigation (API-STANDARD.md §4), so every request a
  // screen makes shares an id and a support conversation has one thing to look
  // up rather than five.
  useEffect(() => {
    beginUserAction();
  }, [location]);

  if (status === "initializing") return <FullPageLoader message="Starting skyData…" />;

  // Called, not rendered as a component: the loader stays the same element as the one shown
  // while access loads, so it is not mounted afresh.
  if (status !== "signed-in") return signedOutView(location, autoSignIn);

  const idleDialog = (
    <IdleTimeoutDialog
      open={idle.isWarning}
      secondsRemaining={idle.secondsRemaining}
      onContinue={idle.continueSession}
      onSignOut={idle.signOutNow}
    />
  );

  if (isLoading) return <FullPageLoader message="Loading your access…" />;

  // A refused sign-in, a second factor still to give, or `/me` failing (`accessProblemView`).
  const problem = accessProblemView({ error, me, signOut, idleDialog });
  if (problem !== null) return problem;

  // A signed-in user landing on the entry routes goes to their first allowed
  // section — unless a privileged save sent them to the identity provider from a
  // screen still holding unsaved work, in which case the redirect comes back
  // here and the staged entry names the screen that is waiting for it (FR-14).
  if (location === "/callback" || location === "/sign-in") {
    return (
      <ReauthRedirect account={account?.email ?? null} instanceId={selectedInstance?.id ?? null} />
    );
  }

  // Round 9 T-58: the address names the instance (`/i/<id>/...`, `lib/instancePath.ts`).
  const addressed = instanceAddressView({ location, search, addressInstance, idleDialog });
  if (addressed !== null) return addressed;

  const legacy = legacyAddressRedirect({ location, search, pendingInstanceId, selectedInstance });
  if (legacy !== null) return legacy;

  return signedInScreenView({
    location,
    search,
    selectedInstance,
    hasPlatformScope,
    mcpEnabled: me?.features?.mcp === true,
    canAny,
    idleDialog,
  });
}

/**
 * No screen raises a toast (Session 10, ux-s10 §3: a notice under the page title, or an error
 * beside the control). A future toast, for an event that finishes while the user is on another
 * screen, lands at the top centre, near where the eye already is, never bottom right. A module
 * constant, so the provider's config is one object for the life of the app.
 */
export const TOAST_CONFIG = { position: "top-center" } as const;

/**
 * A notice belongs to the instance it was posted under (review r2 SF4). Switching instance in
 * the top bar keeps the route, so the provider's clear-on-navigation does not fire; without this
 * "Connection saved. ... Import now" would stay on screen under the new instance, naming the old
 * instance's connection and acting on its ids. Every notice is dismissed when the selected
 * instance changes; the first instance a page load resolves is not a change.
 */
function NoticesFollowInstance() {
  const { selectedInstance } = useTenantInstance();
  const { dismiss } = useNotice();
  const { notices } = useNoticeState();
  const instanceId = selectedInstance?.id ?? null;
  const shown = useRef<string | null>(instanceId);

  useEffect(() => {
    if (instanceId === null || shown.current === instanceId) return;
    const previous = shown.current;
    shown.current = instanceId;
    if (previous === null) return;
    for (const notice of notices) dismiss(notice.id);
  }, [instanceId, notices, dismiss]);

  return null;
}

/**
 * One SkyProvider at the root with the Skypoint brand colour (UX-2,
 * GUIDELINES.md §3.3). It themes `<html>` rather than a wrapper, so Radix
 * portals inherit dark mode — which also means nesting a second provider would
 * not scope anything. `defaultMode` is seeded from the same stored preference
 * the pre-paint guard reads, so the first frame and the first render agree.
 */
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SkyProvider
        brandColor="#FFB31C"
        defaultMode={readStoredPreference()}
        toastConfig={TOAST_CONFIG}
      >
        <ThemeProvider>
          <AuthProvider>
            <TenantInstanceProvider>
              <NoticeProvider>
                <NoticesFollowInstance />
                <InstanceMismatchRecovery />
                <StepUpHost />
                <InstanceRunFeedProvider>
                  <Router>
                    <AppContent />
                  </Router>
                </InstanceRunFeedProvider>
              </NoticeProvider>
            </TenantInstanceProvider>
          </AuthProvider>
        </ThemeProvider>
      </SkyProvider>
    </QueryClientProvider>
  );
}

// Page views and render timings go to Application Insights when it is
// configured; without a connection string the logger returns no plugin and the
// app mounts unwrapped (GUIDELINES.md §4).
export default reactPlugin ? withAITracking(reactPlugin, App) : App;
